import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ProposalCard } from '../seller/SellerAgentView';

// ---------------------------------------------------------------------------
// Business Core > MCP: conexões do MCP do IntegraIA (Claude, ChatGPT, Cursor...). Cada chave
// tem permissão por área do app (desligado / só ler / ler e alterar) e a opção de aprovar
// sozinha. O que os clientes pedem para alterar aparece aqui para aprovar.
// Backend: /api/mcp-connections (routes/mcpRoutes.ts). Ver docs/MCP_INTEGRAIA.md.
// ---------------------------------------------------------------------------

export type Level = 'off' | 'read' | 'write';
export interface Area { key: string; label: string; help: string }
interface McpKey { id: string; name: string; keyPrefix: string; userName?: string | null; autoApprove: boolean; scopes: Record<string, Level>; lastUsedAt?: string | null; revokedAt?: string | null; createdAt: string }
interface McpRequest { id: string; sessionId: string; tool: string; title: string; preview: any; status: string; error?: string | null; result?: any; decidedBy?: string | null; decidedAt?: string | null; createdAt: string; connection: string }

function headers() {
  const token = localStorage.getItem('auth_token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

async function call<T = any>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api/mcp-connections${path}`, { ...init, headers: headers() });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json?.success === false) throw new Error(json?.message || `Erro ${res.status}`);
  return json;
}

const LEVELS: Array<{ key: Level; label: string }> = [
  { key: 'off', label: 'Sem acesso' },
  { key: 'read', label: 'Só ler' },
  { key: 'write', label: 'Ler e alterar' },
];

const STATUS_LABEL: Record<string, string> = { done: 'Aplicado', failed: 'Falhou', rejected: 'Recusado', executing: 'Aplicando', superseded: 'Substituído' };

export function ScopeEditor({ areas, value, onChange }: { areas: Area[]; value: Record<string, Level>; onChange: (next: Record<string, Level>) => void }) {
  const setAll = (lvl: Level) => onChange(Object.fromEntries(areas.map(a => [a.key, lvl])));
  return (
    <div className="rounded-xl border border-slate-200">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-3 py-2">
        <span className="text-xs font-semibold text-slate-700">O que esta conexão pode fazer</span>
        <span className="flex gap-1 text-[11px]">
          {LEVELS.map(l => <button key={l.key} type="button" onClick={() => setAll(l.key)} className="rounded px-1.5 py-0.5 text-slate-500 hover:bg-slate-100">tudo: {l.label.toLowerCase()}</button>)}
        </span>
      </div>
      <div className="divide-y divide-slate-100">
        {areas.map(a => (
          <div key={a.key} className="flex flex-col gap-2 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="text-xs font-medium text-slate-800">{a.label}</p>
              <p className="text-[11px] text-slate-500">{a.help}</p>
            </div>
            <div className="inline-flex shrink-0 rounded-lg bg-slate-100 p-0.5">
              {LEVELS.map(l => {
                const active = (value[a.key] || 'off') === l.key;
                const tone = l.key === 'write' ? 'text-emerald-700' : l.key === 'read' ? 'text-sky-700' : 'text-slate-600';
                return (
                  <button key={l.key} type="button" onClick={() => onChange({ ...value, [a.key]: l.key })}
                    className={`rounded-md px-2.5 py-1 text-[11px] font-semibold transition-all ${active ? `bg-white shadow-sm ${tone}` : 'text-slate-400 hover:text-slate-600'}`}>
                    {l.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function scopeSummary(areas: Area[], scopes: Record<string, Level>) {
  const w = areas.filter(a => scopes[a.key] === 'write').map(a => a.label);
  const r = areas.filter(a => scopes[a.key] === 'read').map(a => a.label);
  return [w.length ? `Altera: ${w.join(', ')}` : '', r.length ? `Só lê: ${r.join(', ')}` : ''].filter(Boolean).join(' · ') || 'Sem acesso a nada';
}

function KeyRow({ k, areas, onChanged }: { k: McpKey; areas: Area[]; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const [scopes, setScopes] = useState<Record<string, Level>>(k.scopes);
  const [saving, setSaving] = useState(false);
  useEffect(() => setScopes(k.scopes), [k.scopes]);
  const dirty = JSON.stringify(scopes) !== JSON.stringify(k.scopes);

  const save = async () => {
    setSaving(true);
    try { await call(`/keys/${k.id}`, { method: 'PATCH', body: JSON.stringify({ scopes }) }); toast.success('Permissões salvas'); onChanged(); }
    catch (e: any) { toast.error(e.message); } finally { setSaving(false); }
  };
  const toggleAuto = async () => {
    const next = !k.autoApprove;
    if (next && !confirm(`Ligar aprovação automática em "${k.name}"? As alterações pedidas por ela passam a ser aplicadas sem a sua aprovação (inclusive o que gasta créditos).`)) return;
    try { await call(`/keys/${k.id}`, { method: 'PATCH', body: JSON.stringify({ autoApprove: next }) }); onChanged(); } catch (e: any) { toast.error(e.message); }
  };
  const revoke = async () => {
    if (!confirm(`Revogar "${k.name}"? Quem usa perde o acesso na hora.`)) return;
    try { await call(`/keys/${k.id}`, { method: 'DELETE' }); onChanged(); } catch (e: any) { toast.error(e.message); }
  };

  return (
    <div className="px-4 py-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-800">
            {k.name} <span className="font-mono text-xs font-normal text-slate-400">{k.keyPrefix}…</span>
            {k.autoApprove && !k.revokedAt && <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">aprova sozinha</span>}
            {k.revokedAt && <span className="ml-1.5 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">revogada</span>}
          </p>
          <p className="text-[11px] text-slate-500">
            {k.revokedAt ? `Revogada em ${new Date(k.revokedAt).toLocaleDateString('pt-BR')}` : k.lastUsedAt ? `Último uso ${new Date(k.lastUsedAt).toLocaleString('pt-BR')}` : 'Nunca usada'}{k.userName ? ` · criada por ${k.userName}` : ''}
          </p>
          {!k.revokedAt && <p className="mt-0.5 text-[11px] text-slate-600">{scopeSummary(areas, k.scopes)}</p>}
        </div>
        {!k.revokedAt && (
          <span className="flex shrink-0 items-center gap-3 text-[11px] font-medium">
            <button type="button" onClick={() => setOpen(o => !o)} className="text-emerald-700 hover:underline">{open ? 'Fechar' : 'Permissões'}</button>
            <button type="button" onClick={toggleAuto} className="text-slate-600 hover:underline">{k.autoApprove ? 'Pedir aprovação' : 'Aprovar sozinha'}</button>
            <button type="button" onClick={revoke} className="text-rose-600 hover:underline">Revogar</button>
          </span>
        )}
      </div>
      {open && !k.revokedAt && (
        <div className="mt-3 space-y-2">
          <ScopeEditor areas={areas} value={scopes} onChange={setScopes} />
          <div className="flex justify-end gap-2">
            {dirty && <button type="button" onClick={() => setScopes(k.scopes)} className="rounded-lg px-3 py-1.5 text-xs text-slate-500">Desfazer</button>}
            <button type="button" disabled={!dirty || saving} onClick={save} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-40">Salvar permissões</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Snippet({ title, text, onCopy, pre }: { title: string; text: string; onCopy: (t: string) => void; pre?: boolean }) {
  return (
    <div>
      <p className="mb-1 text-xs font-semibold text-slate-700">{title}</p>
      <div className="flex gap-2">
        {pre
          ? <pre className="min-w-0 flex-1 overflow-auto rounded-lg bg-slate-900 px-2.5 py-2 text-[11px] text-slate-100">{text}</pre>
          : <code className="min-w-0 flex-1 break-all rounded-lg bg-slate-900 px-2.5 py-2 text-[11px] text-slate-100">{text}</code>}
        <button type="button" onClick={() => onCopy(text)} className="self-start rounded-lg border border-slate-200 px-2.5 py-1.5 text-[11px] hover:bg-slate-50">Copiar</button>
      </div>
    </div>
  );
}

export function McpConnectionsView() {
  const [areas, setAreas] = useState<Area[]>([]);
  const [keys, setKeys] = useState<McpKey[]>([]);
  const [requests, setRequests] = useState<{ pending: McpRequest[]; recent: McpRequest[] }>({ pending: [], recent: [] });
  const [name, setName] = useState('');
  const [newScopes, setNewScopes] = useState<Record<string, Level>>({});
  const [autoApprove, setAutoApprove] = useState(false);
  const [created, setCreated] = useState<{ key: string; name: string; autoApprove?: boolean } | null>(null);
  const [showRevoked, setShowRevoked] = useState(false);
  const [deciding, setDeciding] = useState(false);
  const url = `${window.location.origin}/api/mcp`;

  const load = useCallback(() => {
    call('/keys').then(r => setKeys(r.data || [])).catch(e => toast.error(e.message));
    call('/requests').then(r => setRequests(r.data || { pending: [], recent: [] })).catch(() => null);
  }, []);

  useEffect(() => {
    call('/areas').then(r => {
      const list: Area[] = r.data || [];
      setAreas(list);
      setNewScopes(Object.fromEntries(list.map(a => [a.key, 'read' as Level])));
    }).catch(e => toast.error(e.message));
    load();
  }, [load]);

  // Pedidos novos chegam enquanto o cliente MCP trabalha: atualiza sozinho.
  useEffect(() => {
    const t = setInterval(() => call('/requests').then(r => setRequests(r.data || { pending: [], recent: [] })).catch(() => null), 15000);
    return () => clearInterval(t);
  }, []);

  const create = async () => {
    if (!Object.values(newScopes).some(l => l !== 'off')) { toast.error('Libere pelo menos uma área.'); return; }
    if (autoApprove && !confirm('Com a aprovação automática, as alterações pedidas por esta conexão (inclusive o que gasta créditos) são aplicadas na hora, sem passar por você. Continuar?')) return;
    try {
      const r = await call('/keys', { method: 'POST', body: JSON.stringify({ name: name || 'Claude', autoApprove, scopes: newScopes }) });
      setCreated({ key: r.data.key, name: r.data.name, autoApprove: r.data.autoApprove });
      setName('');
      setAutoApprove(false);
      load();
    } catch (e: any) { toast.error(e.message); }
  };

  const decide = async (req: McpRequest, decision: 'approve' | 'reject', options: Record<string, boolean>, reason?: string) => {
    setDeciding(true);
    try {
      await call(`/requests/${req.sessionId}/${req.id}/decide`, { method: 'POST', body: JSON.stringify({ decision, options, reason }) });
      toast.success(decision === 'approve' ? 'Aprovado: aplicando…' : 'Recusado');
      setTimeout(load, 1500);
    } catch (e: any) { toast.error(e.message); } finally { setDeciding(false); }
  };

  const copy = (text: string) => navigator.clipboard?.writeText(text).then(() => toast.success('Copiado'), () => toast.error('Não consegui copiar'));
  const keyText = created?.key || '<SUA_CHAVE>';
  const claudeCode = `claude mcp add --transport http integraia ${url} --header "Authorization: Bearer ${keyText}"`;
  const desktop = JSON.stringify({ mcpServers: { integraia: { command: 'npx', args: ['-y', 'mcp-remote', url, '--header', `Authorization: Bearer ${keyText}`] } } }, null, 2);
  const active = keys.filter(k => !k.revokedAt);
  const revoked = keys.filter(k => k.revokedAt);

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-600">MCP</p>
        <h2 className="text-base font-black text-slate-900">Conecte o IntegraIA ao Claude, ChatGPT, Cursor e outros agentes</h2>
        <p className="mt-1 text-sm text-slate-600">
          O agente de fora usa as ferramentas do app: Seller, Criar Conteúdo IA (ele pode abrir uma conversa e mandar os agentes de lá criarem), Brand, avatares, perfis de inspiração, fotos, vídeos e posts. Você escolhe o que cada conexão pode ler e alterar. O que ele pedir para alterar aparece em "Pedidos para aprovar", aqui embaixo.
        </p>
      </div>

      {requests.pending.length > 0 && (
        <div className="rounded-2xl border-2 border-amber-300 bg-amber-50/40 p-5">
          <h3 className="text-sm font-bold text-slate-900">Pedidos para aprovar <span className="ml-1 rounded-full bg-amber-500 px-2 py-0.5 text-[11px] text-white">{requests.pending.length}</span></h3>
          <p className="mb-3 text-[11px] text-slate-500">Nada foi alterado ainda. Aprovar aplica na hora; recusar avisa o agente (com o motivo, se você escrever).</p>
          <div className="grid gap-3 lg:grid-cols-2">
            {requests.pending.map(r => (
              <div key={r.id}>
                <p className="mb-1 text-[11px] text-slate-500">{r.connection} · {new Date(r.createdAt).toLocaleString('pt-BR')}</p>
                <ProposalCard
                  ev={{ id: 0, type: 'proposal', payload: { proposalId: r.id, tool: r.tool, title: r.title, preview: r.preview, status: r.status }, createdAt: r.createdAt, updatedAt: r.createdAt }}
                  onDecide={(_id, decision, options, reason) => decide(r, decision, options, reason)}
                  busy={deciding}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-2">
        <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-bold text-slate-900">Nova conexão por chave</h3>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Nome (ex.: Claude do João)" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          {areas.length > 0 && <ScopeEditor areas={areas} value={newScopes} onChange={setNewScopes} />}
          <label className={`flex cursor-pointer items-start gap-2.5 rounded-lg border p-3 ${autoApprove ? 'border-amber-300 bg-amber-50' : 'border-slate-200'}`}>
            <input type="checkbox" checked={autoApprove} onChange={e => setAutoApprove(e.target.checked)} className="mt-0.5 h-4 w-4" />
            <span>
              <span className="block text-xs font-semibold text-slate-800">Aprovar sozinha as alterações</span>
              <span className="block text-[11px] text-slate-500">Ligado: o que a conexão pedir nas áreas "Ler e alterar" é aplicado na hora (inclusive o que gasta créditos). As travas continuam valendo e o que pede escolha ainda espera você. Desligado: tudo vira pedido para aprovar aqui.</span>
            </span>
          </label>
          <button type="button" onClick={create} className="w-full rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700">Criar chave</button>
          {created && (
            <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50 p-3">
              <p className="text-xs font-semibold text-emerald-900">Chave "{created.name}" criada{created.autoApprove ? ' com aprovação automática' : ''}. Copie agora: ela não aparece de novo.</p>
              <div className="mt-2 flex gap-2">
                <code className="min-w-0 flex-1 break-all rounded bg-white px-2 py-1 text-[11px]">{created.key}</code>
                <button type="button" onClick={() => copy(created.key)} className="rounded border border-emerald-300 px-2 text-[11px] font-semibold text-emerald-800">Copiar</button>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-bold text-slate-900">Como conectar</h3>
          <div className="rounded-lg bg-slate-50 p-3">
            <p className="text-xs font-semibold text-slate-700">ChatGPT (sem chave: entra com login)</p>
            <ol className="mt-1 list-decimal space-y-0.5 pl-4 text-[11px] text-slate-600">
              <li>Configurações › Apps e conectores › Avançado › ligue o <b>modo desenvolvedor</b>.</li>
              <li>Crie um conector com a URL abaixo e autenticação <b>OAuth</b>.</li>
              <li>Na tela do IntegraIA que abrir, escolha as áreas e clique em <b>Permitir</b>. A conexão aparece na lista abaixo.</li>
            </ol>
          </div>
          <Snippet title="URL do MCP" text={url} onCopy={copy} />
          <Snippet title="Claude Code" text={claudeCode} onCopy={copy} />
          <Snippet title="Claude Desktop (claude_desktop_config.json)" text={desktop} onCopy={copy} pre />
          <p className="text-[11px] text-slate-400">Conexões antigas em /api/seller/mcp continuam funcionando.</p>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <h3 className="text-sm font-bold text-slate-900">Conexões ({active.length})</h3>
          {revoked.length > 0 && <button type="button" onClick={() => setShowRevoked(v => !v)} className="text-[11px] text-slate-500 hover:underline">{showRevoked ? 'Esconder' : 'Mostrar'} revogadas ({revoked.length})</button>}
        </div>
        {active.length === 0 && !showRevoked ? <p className="px-4 py-4 text-xs text-slate-400">Nenhuma conexão ainda.</p> : (
          <div className="divide-y divide-slate-100">
            {[...active, ...(showRevoked ? revoked : [])].map(k => <KeyRow key={k.id} k={k} areas={areas} onChanged={load} />)}
          </div>
        )}
      </div>

      {requests.recent.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <h3 className="border-b border-slate-100 px-4 py-3 text-sm font-bold text-slate-900">Últimos pedidos decididos</h3>
          <div className="divide-y divide-slate-100">
            {requests.recent.map(r => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-xs">
                <span className="min-w-0 text-slate-700"><b>{r.title}</b> <span className="text-slate-400">· {r.connection}</span></span>
                <span className={`shrink-0 font-semibold ${r.status === 'done' ? 'text-emerald-700' : r.status === 'failed' ? 'text-rose-700' : 'text-slate-500'}`}>
                  {STATUS_LABEL[r.status] || r.status}{r.decidedBy ? ` · ${r.decidedBy}` : ''}{r.status === 'failed' && r.error ? `: ${r.error.slice(0, 120)}` : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
